import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ISignupFormProps } from "../../interfaces/account/signup";
import { IError } from "../../interfaces/user/errors";
import { toast } from "react-toastify";
import { checkUsername, signup } from "../../data-access/apis/authentication/auth";
import { AppDispatch } from "../../store";
import { resetAuth } from "../../stores/auth-slice";

const signupFormProps: ISignupFormProps = {
  name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
}

export const SignupHelper = () => {
  // State for managing the checked status of the checkbox for password criteria.
  // const [isChecked, setIsChecked] = useState(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const signupRequestInProgress = useRef(false);
  const account = useSelector((state: any) => state.auth);
  const dispatch: AppDispatch = useDispatch();
  // Initialize errors state with an empty object for each field
  const [formErrors, setFormErrors] = useState<IError[]>([]);
  const [values, setValues] = useState<ISignupFormProps>(signupFormProps);
  const navigate = useNavigate(); // Initialize navigate from useNavigate
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null); // Timer for debounce
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null); // To track username availability

  // this is validation for every field is empty
  const isFieldEmpty = (field: string) => {
    return field.trim() === '';
  };

  // This is the debounced function for checking username
  const checkUsernameDebounced = (username: string) => {
    if (usernameCheckTimeout) {
      clearTimeout(usernameCheckTimeout); // Clear the previous timeout
    }

    // Set a new timeout to call checkUsername after 2 seconds
    const timeout = setTimeout(async () => {
      try {
        const result = await dispatch(checkUsername(username)).unwrap(); // Dispatch the action
        setUsernameAvailable(result.available); // Assuming the response contains 'available'
      } catch (error: any) {
        setUsernameAvailable(false); // Handle error
        if (error.status === 409) {
          toast.error(error.message || "Error checking username availability, please try again", {
            position: 'top-center',
            autoClose: 3000,
          });
          dispatch(resetAuth());
        }
      }
    }, 2000); // 2 seconds delay

    setUsernameCheckTimeout(timeout); // Store the new timeout
  };

  // Handle username input change and trigger debounce
  const handleUsernameChange = (e: any) => {
    const { name, value } = e.target;
    setValues((preData) => ({
      ...preData,
      [name]: value,
    }));

    if (name === "username") {
      checkUsernameDebounced(value); // Trigger the debounced function for username
    }
  };




  // this is validation for email
  const validateEmail = (email: string): IError | null => {
    if (isFieldEmpty(email)) {
      return { field: 'email', message: 'Please Provide An Email.' };
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      return { field: 'email', message: 'Please enter a valid email address.' };
    } else if (!email.endsWith('gmail.com')) {
      return { field: 'email', message: 'Only Gmail addresses are allowed.' };
    }

    return null; // Return null if email is valid
  };

  // Phone Number Validation: Only allows exactly 10 digits.
  const validatePhoneNumber = (phone: string | number): IError | null => {
    const phoneStr = String(phone); // Convert to string

    if (isFieldEmpty(phoneStr)) {
      return { field: 'phone', message: 'Please provide a phone number.' };
    } else if (!/^\d{10}$/.test(phoneStr)) {
      return { field: 'phone', message: 'Phone number must be exactly 10 digits.' };
    }

    return null; // Return null if phone number is valid
  };

  // Username Validation: No spaces allowed between letters or words.
  const validateUsername = (username: string): IError | null => {
    if (isFieldEmpty(username)) {
      return { field: 'username', message: 'Please provide a username.' };
    } else if (/\s/.test(username)) {
      return { field: 'username', message: 'Username must not contain spaces.' };
    }

    return null; // Return null if username is valid
  };

  const handleSignup = async (e: React.FormEvent | any) => {
    e.preventDefault();

    // Prevent duplicate signup requests from rapid or repeated submits.
    if (signupRequestInProgress.current) return;

    // Reset form errors before validation
    const errors: IError[] = [];

    // Validate name field
    if (isFieldEmpty(values.name)) {
      errors.push({ field: 'name', message: 'Please Provide Your Name.' });
    }

    // Validate username field
    const usernameError = validateUsername(values.username);
    if (usernameError) {
      errors.push(usernameError);
    }

    // Validate email field
    const emailError = validateEmail(values.email);
    if (emailError) {
      errors.push(emailError);
    }

    // Validate phone number field
    const phoneError = validatePhoneNumber(values.phone);
    if (phoneError) {
      errors.push(phoneError);
    }

    // Validate password field
    if (isFieldEmpty(values.password)) {
      errors.push({ field: 'password', message: 'Please Provide Password.' });
    }



    // Update form errors state and only proceed if there are no errors
    setFormErrors(errors);

    if (errors.length === 0) {
      signupRequestInProgress.current = true;
      setIsProcessing(true);

      // Save email in localStorage with expiry of 30 mins
      const expiryTime = new Date().getTime() + 30 * 60 * 1000; // current time + 30 minutes
      const emailData = {
        email: values.email,
        expiry: expiryTime
      };
      localStorage.setItem('signup_email', JSON.stringify(emailData));

    } else {
      // Show toast messages for each error
      errors.forEach((error) => {
        toast.error(`${error.message}`, {
          position: 'bottom-right', // You can customize the toast position
          autoClose: 3000, // Specify how long the toast should be displayed (in milliseconds)
        });
      });
      return; // Exit early if there are errors
    }

    try {
      if (errors.length === 0) {
        const response = await dispatch(signup(values)).unwrap();
        toast.success(response?.message || "Account created. Check your email for the OTP.", {
          position: "bottom-right",
          autoClose: 3500,
          toastId: "signup-success",
        });
      }
    } catch (error: any) {
      const message =
        error?.data?.message ||
        error?.message ||
        (typeof error === "string" ? error : null) ||
        "Signup failed. Please try again.";
      toast.error(message, {
        position: "bottom-right",
        autoClose: 5000,
        toastId: "signup-error",
      });
      // Keep the form values so the user can correct the issue or retry.
    } finally {
      signupRequestInProgress.current = false;
      setIsProcessing(false);
    }
  };


  // Effect hook to dispatch action when form is submitted and there are no errors
  useEffect(() => {
    if (account.statusCode === 201) {
      setIsProcessing(false);
      setValues(signupFormProps);  // Reset the form fields
      navigate('/auth/verify-otp');
      dispatch(resetAuth());
    }
  }, [account.statusCode, navigate, dispatch])

  // const handleChange = (e: any) => {
  //   const { name, value } = e.target;
  //   setValues((preData: any) => ({
  //     ...preData,
  //     [name]: value,
  //   }));
  // };

  return {
    values,
    handleChange: handleUsernameChange,
    formErrors,
    handleSignup,
    isProcessing,
    // handleChange: handleUsernameChange, // Update this to handle username change separately
    usernameAvailable, // Expose this state to the component
    // isChecked, 
    // setIsChecked
  };
};
